/**
 * Do-Not-Contact toggle for a call sign.
 *
 * Two presentations of the same control so search results and detail pages can't
 * drift apart:
 *   - `variant="icon"`  — a compact icon button for a dense table row.
 *   - `variant="button"` — an labelled button for a detail page, where there is
 *     room to say what it does and no column header to explain the icon.
 *
 * The caller owns the DNC state (search already tracks a Set of flagged call
 * signs) and is told the new value via `onChange`, so this component stays
 * presentational apart from the toggle request itself.
 */
import { useState } from 'react'
import { toggleDnc } from '../services/crm'
import Modal, { ModalFooter } from './Modal'

/**
 * Reasons offered as one-click choices.
 *
 * The list exists because a reason box that is only free text gets left blank on a
 * busy day, and a blank reason is how the Do Not Contact list ends up saying *who*
 * and never *why* — which is exactly the state entries added from search results
 * were already in, since the toggle never collected one. Free text stays available
 * for everything these do not cover.
 */
const COMMON_REASONS = [
  'Asked not to be contacted',
  'Already has a provider',
  'Competitor',
  'Wrong contact details',
  'Out of business',
]

/** Phone with a slash — "do not call", rather than a bare "DNC" abbreviation. */
function NoCallIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
      />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3l18 18" />
    </svg>
  )
}

interface DncToggleProps {
  /** The call sign this control flags. */
  callSign: string
  /** Whether the call sign is currently on the Do Not Contact list. */
  isDnc: boolean
  /** Called with the new state after a successful toggle. */
  onChange: (isDnc: boolean) => void
  /** Called with a human-readable message after a successful toggle. */
  onMessage?: (message: string) => void
  /** Called if the toggle request fails. */
  onError?: (message: string) => void
  variant?: 'icon' | 'button'
}

export default function DncToggle({
  callSign,
  isDnc,
  onChange,
  onMessage,
  onError,
  variant = 'icon',
}: DncToggleProps) {
  const [saving, setSaving] = useState(false)
  const [asking, setAsking] = useState(false)
  const [reason, setReason] = useState('')

  const label = isDnc ? 'Remove from Do Not Contact' : 'Mark Do Not Contact'
  const title = isDnc
    ? `${callSign} is on the Do Not Contact list — click to remove it`
    : `Add ${callSign} to the Do Not Contact list`

  /**
   * Turning it ON asks why; turning it OFF does not.
   *
   * The asymmetry is deliberate. A reason is what the list was missing, and the
   * moment somebody knows it is the moment they add the entry. Asking again on the
   * way out would put a dialog in front of the one action people take when they have
   * realised they made a mistake — and removals are already audited, and now reach
   * the admin review badge.
   */
  async function handleClick(e: React.MouseEvent) {
    // Detail pages may render this inside a clickable row/card.
    e.stopPropagation()
    if (saving) return
    if (!isDnc) {
      setReason('')
      setAsking(true)
      return
    }
    await submit()
  }

  async function submit(withReason?: string) {
    setSaving(true)
    try {
      const result = await toggleDnc({
        match_type: 'call_sign',
        value: callSign,
        reason: withReason?.trim() || undefined,
      })
      onChange(result.is_dnc)
      onMessage?.(
        result.is_dnc
          ? `${callSign} added to Do Not Contact list`
          : `${callSign} removed from Do Not Contact list`
      )
      setAsking(false)
    } catch (err) {
      onError?.(err instanceof Error ? err.message : 'Failed to update Do Not Contact')
      setAsking(false)
    } finally {
      setSaving(false)
    }
  }

  /** The reason prompt. Skippable on purpose — a required field here would be
      answered with "." within a week, and a blank reason is more honest than a
      fake one. */
  const prompt = asking ? (
    <Modal
      titleId="dnc-reason-title"
      title={`Add ${callSign} to Do Not Contact?`}
      subtitle="Recording why helps whoever sees this next. You can skip it."
      onClose={() => setAsking(false)}
      maxWidth="max-w-md"
    >
      <div className="flex flex-wrap gap-1.5 mb-3">
        {COMMON_REASONS.map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => setReason(r)}
            className={`px-2 py-1 text-xs rounded border ${
              reason === r
                ? 'border-red-300 bg-red-50 text-red-700'
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {r}
          </button>
        ))}
      </div>
      <label htmlFor="dnc-reason-input" className="block text-xs text-gray-500 mb-1">
        Reason (optional)
      </label>
      <input
        id="dnc-reason-input"
        className="input"
        value={reason}
        maxLength={2000}
        autoFocus
        placeholder="e.g. asked us not to call, 21 Sep"
        onChange={(ev) => setReason(ev.target.value)}
        onKeyDown={(ev) => { if (ev.key === 'Enter') void submit(reason) }}
      />
      <ModalFooter>
        <button type="button" className="btn btn-secondary" onClick={() => setAsking(false)}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={saving}
          onClick={() => void submit(reason)}
        >
          {saving ? 'Saving…' : 'Add to Do Not Contact'}
        </button>
      </ModalFooter>
    </Modal>
  ) : null

  if (variant === 'icon') {
    return (
      <>
      {prompt}
      <button
        type="button"
        title={title}
        aria-label={label}
        aria-pressed={isDnc}
        disabled={saving}
        onClick={handleClick}
        className={`p-2 rounded disabled:opacity-50 ${
          isDnc
            ? 'text-red-600 hover:text-red-800 hover:bg-red-50'
            : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
        }`}
      >
        <NoCallIcon className="h-4 w-4" />
      </button>
      </>
    )
  }

  return (
    <>
    {prompt}
    <button
      type="button"
      title={title}
      aria-pressed={isDnc}
      disabled={saving}
      onClick={handleClick}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded border disabled:opacity-50 ${
        isDnc
          ? 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100'
          : 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:text-red-600 hover:border-red-300'
      }`}
    >
      <NoCallIcon className="h-4 w-4" />
      {saving ? 'Saving…' : label}
    </button>
    </>
  )
}

/** Badge marking a record as Do Not Contact. Shared so the styling is identical. */
export function DncBadge({ className = '' }: { className?: string }) {
  return (
    <span
      title="This license is on the Do Not Contact list"
      className={`inline-flex items-center gap-1 text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded align-middle font-semibold ${className}`}
    >
      <NoCallIcon className="h-3 w-3" />
      DNC
    </span>
  )
}
